import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {RightPanelOverlayHeaderProps} from './right-panel-overlay-header.types';
import styles from './right-panel-overlay-header.module.scss';

export function RightPanelOverlayHeader({title, backAriaLabel, onBack, onClose}: RightPanelOverlayHeaderProps): ReactElement {
  return (
    <header className={styles.header}>
      {renderLeading(onBack, backAriaLabel, onClose)}
      <Text val={title} size={TextSize.LG} bold className={styles.title} />
      {renderTrailing(onBack, onClose)}
    </header>
  );
}

/** Leading slot: a back arrow when the overlay can go back, otherwise a spacer that balances a trailing close (and nothing when the header has no side controls, so the title centres full-width). */
function renderLeading(onBack: (() => void) | undefined, backAriaLabel: string | undefined, onClose: (() => void) | undefined): ReactElement | null {
  if (onBack) {
    return (
      <Icon
        icon="icon-arrow-left"
        type={IconType.GHOST_UNCOLORED}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        ariaLabel={backAriaLabel ?? window.i18next.t('pmwjs_back')}
        isUsedAsButton
        onClick={onBack}
      />
    );
  }
  if (onClose) {
    return <div className={styles.spacer} aria-hidden />;
  }
  return null;
}

/** Trailing slot: a close (X) when the overlay can dismiss its whole flow, otherwise a spacer that balances a leading back arrow (and nothing when the header has no side controls). */
function renderTrailing(onBack: (() => void) | undefined, onClose: (() => void) | undefined): ReactElement | null {
  if (onClose) {
    return (
      <Icon
        icon="icon-close"
        type={IconType.GHOST_UNCOLORED}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        ariaLabel={window.i18next.t('pmwjs_close')}
        isUsedAsButton
        onClick={onClose}
      />
    );
  }
  if (onBack) {
    return <div className={styles.spacer} aria-hidden />;
  }
  return null;
}
